import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { DsButton, DsInput, DsLabel, DsSpinner } from "../../../../design-system";
import PolicyApplicabilityStepSection from "../../policy-wizard/PolicyApplicabilityStepSection";
import { hiringService } from "../../../../services/api";
import portalApi from "../api";

const SETTINGS_TABS = [
  { id: "ids", label: "ID formats" },
  { id: "mandatory", label: "Mandatory fields" },
  { id: "policies", label: "Policies & workflow" },
];

const PORTAL_ID_DEFAULTS = {
  application: { prefix: "App", numberOfDigits: 6, startingNumber: 1 },
  job: { prefix: "Job", numberOfDigits: 6, startingNumber: 1 },
  candidate: { prefix: "Cand", numberOfDigits: 6, startingNumber: 1 },
  client: { prefix: "Client", numberOfDigits: 6, startingNumber: 1 },
};

/** Portal stores "App-000001"; hiring UI uses prefix / digits / starting. */
function parsePortalFormat(str, fallback) {
  const m = String(str || "").match(/^([A-Za-z]+)-(\d+)$/);
  if (!m) return { ...fallback };
  return {
    prefix: m[1],
    numberOfDigits: m[2].length,
    startingNumber: Number(m[2].replace(/^0+/, "") || "1") || 1,
  };
}

function toPortalFormat(fmt, fallback) {
  const prefix = (fmt?.prefix || fallback.prefix || "ID").replace(/[^A-Za-z]/g, "") || fallback.prefix;
  const digits = Math.min(10, Math.max(1, Number(fmt?.numberOfDigits) || fallback.numberOfDigits || 6));
  const start = Math.max(1, Number(fmt?.startingNumber) || fallback.startingNumber || 1);
  return `${prefix}-${String(start).padStart(digits, "0")}`;
}

// ponytail: round-trip check for portal ID string ↔ UI fields (ceiling: console assert in DEV only)
if (typeof process !== "undefined" && process.env.NODE_ENV !== "production") {
  const sample = PORTAL_ID_DEFAULTS.application;
  const encoded = toPortalFormat(sample, sample);
  const decoded = parsePortalFormat(encoded, sample);
  console.assert(
    encoded === "App-000001" &&
      decoded.prefix === "App" &&
      decoded.numberOfDigits === 6 &&
      decoded.startingNumber === 1,
    "portal id format round-trip"
  );
}

function emptyPolicy() {
  return {
    name: "",
    effectiveDate: new Date().toISOString().slice(0, 10),
    status: "active",
    approvalMethod: "workflow",
    needWorkflowSteps: true,
    workflowSteps: [{ role: "", label: "Approver 1" }],
    offerWorkflowSteps: [],
    allEmployees: true,
    branch: "",
    department: "",
    subDepartment: "",
    designation: "",
    grade: "",
    category: "",
    location: "",
    employmentTypes: [],
  };
}

function IdFormatRow({ label, fmt, canEdit, onChange }) {
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
      <div className="ds-h4 md:col-span-4">{label}</div>
      <div>
        <DsLabel>Prefix</DsLabel>
        <DsInput
          disabled={!canEdit}
          value={fmt.prefix || ""}
          onChange={(e) => onChange({ ...fmt, prefix: e.target.value })}
        />
      </div>
      <div>
        <DsLabel>Digits</DsLabel>
        <DsInput
          type="number"
          min={1}
          max={10}
          disabled={!canEdit}
          value={fmt.numberOfDigits || 5}
          onChange={(e) =>
            onChange({ ...fmt, numberOfDigits: Number(e.target.value) || 5 })
          }
        />
      </div>
      <div>
        <DsLabel>Starting number</DsLabel>
        <DsInput
          type="number"
          min={1}
          disabled={!canEdit}
          value={fmt.startingNumber || 1}
          onChange={(e) =>
            onChange({ ...fmt, startingNumber: Number(e.target.value) || 1 })
          }
        />
      </div>
      <div className="flex items-end ds-caption text-[hsl(var(--ds-muted-fg))]">
        Preview: {fmt.prefix || "…"}-
        {String(fmt.startingNumber || 1).padStart(fmt.numberOfDigits || 5, "0")}
      </div>
    </div>
  );
}

export default function HiringSettingsPanel({
  canEdit,
  initialTab = "ids",
  hideTabs = false,
}) {
  const [tab, setTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [portalIdFormats, setPortalIdFormats] = useState(PORTAL_ID_DEFAULTS);
  const [meta, setMeta] = useState({ candidateFieldCatalog: [], employeeFieldCatalog: [] });
  const [policies, setPolicies] = useState([]);
  const [policyDraft, setPolicyDraft] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, m, p, portalRes] = await Promise.all([
        hiringService.getConfig(),
        hiringService.getMeta(),
        hiringService.listPolicies(),
        portalApi.get("/settings").catch(() => null),
      ]);
      setConfig(c?.data || c);
      setMeta(m?.data || m || {});
      setPolicies(p?.data || []);
      const portalFmts = portalRes?.data?.idFormats || {};
      setPortalIdFormats(
        Object.fromEntries(
          Object.keys(PORTAL_ID_DEFAULTS).map((key) => [
            key,
            parsePortalFormat(portalFmts[key], PORTAL_ID_DEFAULTS[key]),
          ])
        )
      );
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load hiring settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (["ids", "mandatory", "policies"].includes(initialTab)) {
      setTab(initialTab);
    }
  }, [initialTab]);

  const idEntities = useMemo(
    () => [
      { key: "mrf", label: "MRF ID" },
      { key: "job", label: "Job ID" },
      { key: "candidate", label: "Candidate ID" },
      { key: "application", label: "Application ID" },
    ],
    []
  );

  const portalIdEntities = useMemo(
    () => [
      { key: "application", label: "Portal Application ID" },
      { key: "job", label: "Portal Job ID" },
      { key: "candidate", label: "Portal Candidate ID" },
      { key: "client", label: "Portal Client ID" },
    ],
    []
  );

  const saveConfig = async () => {
    if (!canEdit || !config) return;
    setSaving(true);
    try {
      const res = await hiringService.saveConfig(config);
      setConfig(res?.data || res);
      toast.success("Hiring settings saved");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const saveIdFormats = async () => {
    if (!canEdit || !config) return;
    setSaving(true);
    try {
      const portalPayload = Object.fromEntries(
        Object.keys(PORTAL_ID_DEFAULTS).map((key) => [
          key,
          toPortalFormat(portalIdFormats[key], PORTAL_ID_DEFAULTS[key]),
        ])
      );
      const [res] = await Promise.all([
        hiringService.saveConfig(config),
        portalApi.put("/settings", { idFormats: portalPayload }),
      ]);
      setConfig(res?.data || res);
      toast.success("ID formats saved");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleMandatory = (side, key) => {
    if (!canEdit || !config) return;
    setConfig((prev) => {
      const list = new Set(prev.mandatoryFields?.[side] || []);
      if (list.has(key)) list.delete(key);
      else list.add(key);
      return {
        ...prev,
        mandatoryFields: {
          ...prev.mandatoryFields,
          [side]: Array.from(list),
        },
      };
    });
  };

  const savePolicy = async () => {
    if (!canEdit || !policyDraft) return;
    if (!policyDraft.name.trim()) {
      toast.error("Policy name required");
      return;
    }
    setSaving(true);
    try {
      if (policyDraft.id) {
        await hiringService.updatePolicy(policyDraft.id, policyDraft);
      } else {
        await hiringService.createPolicy(policyDraft);
      }
      toast.success("Policy saved");
      setPolicyDraft(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Policy save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !config) {
    return (
      <div className="flex items-center gap-2 p-6">
        <DsSpinner size="sm" />
        <span className="ds-caption">Loading settings…</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!hideTabs && (
        <>
          <div>
            <h2 className="ds-h3">Hiring settings</h2>
            <p className="ds-caption mt-1 text-[hsl(var(--ds-muted-fg))]">
              ID formats, mandatory profile fields, and hiring policies (workflow +
              applicability). Statutory stays in the existing product settings.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {SETTINGS_TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                className={
                  tab === t.id
                    ? "ds-page-nav-item ds-page-nav-item-active"
                    : "ds-page-nav-item"
                }
                onClick={() => setTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>
        </>
      )}

      {tab === "ids" && (
        <div className="space-y-6 rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-4">
          <div className="space-y-4">
            <p className="ds-caption text-[hsl(var(--ds-muted-fg))]">
              Hiring (MRF / job / candidate / application)
            </p>
            {idEntities.map((ent) => {
              const fmt = config.idFormats?.[ent.key] || {};
              return (
                <IdFormatRow
                  key={ent.key}
                  label={ent.label}
                  fmt={fmt}
                  canEdit={canEdit}
                  onChange={(next) =>
                    setConfig((p) => ({
                      ...p,
                      idFormats: { ...p.idFormats, [ent.key]: next },
                    }))
                  }
                />
              );
            })}
          </div>

          <div className="border-t border-[hsl(var(--ds-border))] pt-4 space-y-4">
            <p className="ds-caption text-[hsl(var(--ds-muted-fg))]">
              Portal (application / job / candidate / client)
            </p>
            {portalIdEntities.map((ent) => {
              const fmt = portalIdFormats[ent.key] || PORTAL_ID_DEFAULTS[ent.key];
              return (
                <IdFormatRow
                  key={`portal-${ent.key}`}
                  label={ent.label}
                  fmt={fmt}
                  canEdit={canEdit}
                  onChange={(next) =>
                    setPortalIdFormats((p) => ({ ...p, [ent.key]: next }))
                  }
                />
              );
            })}
          </div>

          {canEdit && (
            <div className="flex justify-end">
              <DsButton type="button" disabled={saving} onClick={saveIdFormats}>
                {saving ? "Saving…" : "Save ID formats"}
              </DsButton>
            </div>
          )}
        </div>
      )}

      {tab === "mandatory" && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-4">
            <h3 className="ds-h4">Candidate (pre-hire)</h3>
            <p className="ds-caption mb-3 text-[hsl(var(--ds-muted-fg))]">
              Required on the candidate profile invite page
            </p>
            <div className="space-y-2">
              {(meta.candidateFieldCatalog || []).map((f) => (
                <label key={f.key} className="flex items-center gap-2 ds-body">
                  <input
                    type="checkbox"
                    disabled={!canEdit}
                    checked={(config.mandatoryFields?.candidate || []).includes(f.key)}
                    onChange={() => toggleMandatory("candidate", f.key)}
                  />
                  {f.label}
                  <span className="ds-caption text-[hsl(var(--ds-muted-fg))]">
                    ({f.section})
                  </span>
                </label>
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-4">
            <h3 className="ds-h4">Employee (onboard / profile)</h3>
            <p className="ds-caption mb-3 text-[hsl(var(--ds-muted-fg))]">
              Profile tabs required at onboard — statutory remains in product settings
            </p>
            <div className="space-y-2">
              {(meta.employeeFieldCatalog || []).map((f) => (
                <label key={f.key} className="flex items-center gap-2 ds-body">
                  <input
                    type="checkbox"
                    disabled={!canEdit}
                    checked={(config.mandatoryFields?.employee || []).includes(f.key)}
                    onChange={() => toggleMandatory("employee", f.key)}
                  />
                  {f.label}
                </label>
              ))}
            </div>
          </div>
          {canEdit && (
            <div className="lg:col-span-2 flex justify-end">
              <DsButton type="button" disabled={saving} onClick={saveConfig}>
                {saving ? "Saving…" : "Save mandatory fields"}
              </DsButton>
            </div>
          )}
        </div>
      )}

      {tab === "policies" && (
        <div className="space-y-4">
          <div className="flex justify-between gap-2">
            <p className="ds-caption text-[hsl(var(--ds-muted-fg))]">
              MRF approval roles come from policy workflow steps. After final approval,
              the job opens.
            </p>
            {canEdit && (
              <DsButton type="button" onClick={() => setPolicyDraft(emptyPolicy())}>
                New policy
              </DsButton>
            )}
          </div>

          <div className="overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]">
            <table className="ds-table w-full text-left text-sm">
              <thead>
                <tr>
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Effective</th>
                  <th className="px-3 py-2">Approval</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {policies.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-3 py-6 text-center text-[hsl(var(--ds-muted-fg))]">
                      No hiring policies yet — create one to define MRF workflow
                    </td>
                  </tr>
                )}
                {policies.map((p) => (
                  <tr key={p.id} className="border-t border-[hsl(var(--ds-border))]">
                    <td className="px-3 py-2 font-medium">{p.name}</td>
                    <td className="px-3 py-2">{p.effectiveDate}</td>
                    <td className="px-3 py-2">{p.approvalMethod}</td>
                    <td className="px-3 py-2">{p.status}</td>
                    <td className="px-3 py-2">
                      {canEdit && (
                        <DsButton
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => setPolicyDraft({ ...emptyPolicy(), ...p })}
                        >
                          Edit
                        </DsButton>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {policyDraft && (
            <div className="space-y-4 rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-4">
              <h3 className="ds-h4">{policyDraft.id ? "Edit policy" : "New policy"}</h3>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <DsLabel>Name</DsLabel>
                  <DsInput
                    value={policyDraft.name}
                    onChange={(e) =>
                      setPolicyDraft((p) => ({ ...p, name: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <DsLabel>Effective date</DsLabel>
                  <DsInput
                    type="date"
                    value={policyDraft.effectiveDate}
                    onChange={(e) =>
                      setPolicyDraft((p) => ({ ...p, effectiveDate: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <DsLabel>Approval method</DsLabel>
                  <select
                    className="h-9 w-full rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm"
                    value={policyDraft.approvalMethod}
                    onChange={(e) =>
                      setPolicyDraft((p) => ({ ...p, approvalMethod: e.target.value }))
                    }
                  >
                    <option value="workflow">Workflow</option>
                    <option value="auto_approved">Auto approved</option>
                    <option value="on_instance">On instance</option>
                  </select>
                </div>
                <div>
                  <DsLabel>Status</DsLabel>
                  <select
                    className="h-9 w-full rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm"
                    value={policyDraft.status}
                    onChange={(e) =>
                      setPolicyDraft((p) => ({ ...p, status: e.target.value }))
                    }
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div>
                <h4 className="ds-h4 mb-2">MRF workflow steps (role-based)</h4>
                {(policyDraft.workflowSteps || []).map((step, idx) => (
                  <div key={idx} className="mb-2 grid grid-cols-1 gap-2 md:grid-cols-3">
                    <DsInput
                      placeholder="Role code (e.g. reporting_manager)"
                      value={step.role || ""}
                      onChange={(e) => {
                        const next = [...(policyDraft.workflowSteps || [])];
                        next[idx] = { ...next[idx], role: e.target.value };
                        setPolicyDraft((p) => ({ ...p, workflowSteps: next }));
                      }}
                    />
                    <DsInput
                      placeholder="Label"
                      value={step.label || ""}
                      onChange={(e) => {
                        const next = [...(policyDraft.workflowSteps || [])];
                        next[idx] = { ...next[idx], label: e.target.value };
                        setPolicyDraft((p) => ({ ...p, workflowSteps: next }));
                      }}
                    />
                    <DsButton
                      type="button"
                      variant="outline"
                      onClick={() => {
                        const next = (policyDraft.workflowSteps || []).filter(
                          (_, i) => i !== idx
                        );
                        setPolicyDraft((p) => ({ ...p, workflowSteps: next }));
                      }}
                    >
                      Remove
                    </DsButton>
                  </div>
                ))}
                <DsButton
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setPolicyDraft((p) => ({
                      ...p,
                      workflowSteps: [
                        ...(p.workflowSteps || []),
                        { role: "", label: `Approver ${(p.workflowSteps || []).length + 1}` },
                      ],
                    }))
                  }
                >
                  Add step
                </DsButton>
              </div>

              <PolicyApplicabilityStepSection
                policy={policyDraft}
                setPolicy={setPolicyDraft}
                title="Applicability"
                allEmployeesLabel="Applicable to all (company-wide)"
              />

              <div className="flex justify-end gap-2">
                <DsButton type="button" variant="outline" onClick={() => setPolicyDraft(null)}>
                  Cancel
                </DsButton>
                <DsButton type="button" disabled={saving} onClick={savePolicy}>
                  {saving ? "Saving…" : "Save policy"}
                </DsButton>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
